import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { PaginatedResponse, getErrorMessage } from '../types';
import { useTranslation } from '../i18n';
import { formatDateTime } from '../formatDate';
import { ProductCategory } from '../pricing';
import { categoryLabelKey } from '../categories';

interface SearchLogItem {
  id: number;
  username: string | null;
  searchString: string;
  category: ProductCategory | null;
  isWishlist: boolean;
  createdAt: string;
}

const PAGE_SIZE = 50;

export const SearchLogsPage: React.FC = () => {
  const { t, lang } = useTranslation();
  const [logs, setLogs] = useState<SearchLogItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async (pg: number) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(pg),
        pageSize: String(PAGE_SIZE),
      });
      const res = await api.get<PaginatedResponse<SearchLogItem>>(`/search/logs?${params}`);
      setLogs(res.data.items);
      setTotalCount(res.data.totalCount);
    } catch (err) {
      setError(getErrorMessage(err, t('search_logs.load_error')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchLogs(page);
  }, [fetchLogs, page]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div>
      <h2 className="section-title">{t('search_logs.title')}</h2>
      <p className="text-muted">{t('search_logs.description')}</p>

      {error && (
        <div className="alert alert-error">
          {error}
          <button type="button" className="dismiss" onClick={() => setError('')}>&times;</button>
        </div>
      )}

      {loading ? (
        <p className="text-muted">{t('search_logs.loading')}</p>
      ) : logs.length === 0 ? (
        <p className="text-muted">{t('search_logs.empty')}</p>
      ) : (
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('search_logs.col_date')}</th>
                <th>{t('search_logs.col_username')}</th>
                <th>{t('search_logs.col_query')}</th>
                <th>{t('search_logs.col_category')}</th>
                <th>{t('search_logs.col_type')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr
                  key={log.id}
                  className={log.isWishlist ? 'search-log-row--wishlist' : undefined}
                >
                  <td>{formatDateTime(log.createdAt, lang)}</td>
                  <td>{log.username ?? t('search_logs.anonymous')}</td>
                  <td>
                    <code>{log.searchString}</code>
                  </td>
                  <td>
                    {log.category != null
                      ? t(categoryLabelKey(log.category))
                      : '—'}
                  </td>
                  <td>
                    {log.isWishlist ? (
                      <span className="badge badge-wishlist">{t('search_logs.type_wishlist')}</span>
                    ) : (
                      <span className="badge badge-muted">{t('search_logs.type_search')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            {t('products.page_prev')}
          </button>
          <span className="text-muted" style={{ padding: '0 0.75rem' }}>
            {t('search_logs.page_of', { page, totalPages })}
          </span>
          <button
            className="btn btn-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            {t('products.page_next')}
          </button>
        </div>
      )}
    </div>
  );
};
